<template>
  <q-btn
    flat
    round
    class="notif-bell"
    :class="{ 'notif-bell--active': menuOpen }"
    aria-label="Уведомления"
  >
    <span class="notif-bell__icon">
      <q-icon :name="unreadCount > 0 ? 'notifications' : 'notifications_none'" size="22px" />
      <span v-if="unreadCount > 0" class="notif-bell__badge">
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </span>

    <q-menu
      v-model="menuOpen"
      anchor="bottom right"
      self="top right"
      :offset="[0, 8]"
      class="notif-bell__menu"
      @show="load"
    >
      <div class="notif-bell__panel">
        <header class="notif-bell__head">
          <div class="notif-bell__title">
            Уведомления
            <span v-if="unreadCount > 0" class="notif-bell__count">{{ unreadCount }}</span>
          </div>
          <q-btn
            v-if="unreadCount > 0"
            flat
            dense
            no-caps
            label="Прочитать все"
            class="notif-bell__mark-all"
            :loading="markingAll"
            @click="markAllRead"
          />
        </header>

        <div class="notif-bell__tabs">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            class="notif-bell__tab"
            :class="{ 'notif-bell__tab--active': unreadOnly === tab.value }"
            @click="setTab(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="notif-bell__content">
          <q-inner-loading :showing="loading && !items.length" color="primary" />

          <div v-if="!loading && !items.length" class="notif-bell__empty">
            <q-icon name="notifications_off" size="28px" />
            <span>{{ unreadOnly ? "Всё прочитано" : "Уведомлений пока нет" }}</span>
          </div>

          <NotificationItem
            v-for="item in items"
            :key="item.notification_id"
            :item="item"
            compact
            @open="open"
            @mark-read="markRead"
          />
        </div>

        <footer class="notif-bell__foot">
          <router-link :to="PAGES.notificationsInbox" class="notif-bell__all" @click="menuOpen = false">
            Все уведомления
            <q-icon name="chevron_right" size="18px" />
          </router-link>
        </footer>
      </div>
    </q-menu>
  </q-btn>
</template>

<script>
import { PAGES } from "@/constants";
import notificationsApi from "./api";
import NotificationItem from "./NotificationItem.vue";
import useNotifications from "./useNotifications";

const PREVIEW_SIZE = 8;

export default {
  name: "NotificationBell",
  components: { NotificationItem },
  setup() {
    return { notifications: useNotifications() };
  },
  data() {
    return {
      PAGES,
      menuOpen: false,
      loading: false,
      markingAll: false,
      unreadOnly: false,
      items: [],
      tabs: [
        { value: false, label: "Все" },
        { value: true, label: "Непрочитанные" },
      ],
    };
  },
  computed: {
    unreadCount() {
      return this.notifications.state.unreadCount;
    },
  },
  watch: {
    "notifications.state.revision"() {
      if (this.menuOpen) this.load();
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const data = await notificationsApi.getInbox({
          page: 1,
          pageSize: PREVIEW_SIZE,
          unreadOnly: this.unreadOnly,
        });
        this.items = data.items || [];
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    setTab(value) {
      if (this.unreadOnly === value) return;
      this.unreadOnly = value;
      this.items = [];
      this.load();
    },
    async open(item) {
      this.menuOpen = false;
      if (await this.notifications.openNotification(item, this.$router)) item.is_read = true;
    },
    async markRead(item) {
      if (await this.notifications.markRead(item)) item.is_read = true;
    },
    async markAllRead() {
      this.markingAll = true;
      try {
        await this.notifications.markAllRead();
      } finally {
        this.markingAll = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.notif-bell {
  // В шапке блоки растягиваются по высоте — кнопку ставим по центру строки.
  align-self: center;
  width: 42px;
  height: 42px;
  color: #4b5563;

  &--active,
  &:hover {
    color: #0060fe;
  }

  // Бейдж привязан к иконке, а не к кнопке: сидит на её правом верхнем углу
  // и закрывает только край колокольчика.
  &__icon {
    position: relative;
    display: inline-flex;
  }

  &__badge {
    position: absolute;
    top: -7px;
    left: 13px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    border: 2px solid #fff;
    box-sizing: content-box;
    background: #e5252a;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    text-align: center;
  }
}

.notif-bell__panel {
  width: min(400px, calc(100vw - 24px));
  display: flex;
  flex-direction: column;
  max-height: min(560px, calc(100vh - 96px));
  background: #fff;
}

.notif-bell__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px 8px 16px;
}

.notif-bell__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.notif-bell__count {
  padding: 0 8px;
  border-radius: 10px;
  background: #e8f1ff;
  color: #0060fe;
  font-size: 12px;
  line-height: 20px;
  font-weight: 700;
}

.notif-bell__mark-all {
  color: #0060fe;
  font-size: 13px;
  font-weight: 600;
}

.notif-bell__tabs {
  display: flex;
  gap: 4px;
  padding: 0 16px 8px;
  border-bottom: 1px solid #e5e7eb;
}

.notif-bell__tab {
  border: none;
  background: transparent;
  padding: 4px 10px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    background: #f2f5f8;
  }

  &--active {
    background: #e8f1ff;
    color: #0060fe;
  }
}

.notif-bell__content {
  position: relative;
  flex: 1;
  min-height: 120px;
  overflow-y: auto;
}

.notif-bell__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 16px;
  color: #9ca3af;
  font-size: 14px;
}

.notif-bell__foot {
  border-top: 1px solid #e5e7eb;
}

.notif-bell__all {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 12px;
  color: #0060fe;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    background: #f2f5f8;
  }
}
</style>

<style lang="scss">
// q-menu телепортируется в body — scoped сюда не дотягивается.
.notif-bell__menu {
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.14);
}
</style>
